import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { PrimaryButton } from '../components/PrimaryButton';
import { api, apiErrorMessage } from '../../lib/api';

/**
 * Landing page for the marketing-email unsubscribe link. The token in
 * `?t=` is signed by the backend (12-month expiry); we POST it to
 * `/public/marketing/unsubscribe` on mount. UK GDPR Art. 7(3): consent
 * withdrawal must be as easy as giving it — no login required.
 */
export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? '';
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'done'; message: string }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!token) {
      setState({
        kind: 'error',
        message:
          'This unsubscribe link is missing its access token. Open it from the email we sent.',
      });
      return;
    }

    (async () => {
      try {
        const res = await api.post('/public/marketing/unsubscribe', {
          token,
        });
        setState({
          kind: 'done',
          message:
            res.data?.message ??
            "You've been unsubscribed from marketing emails. You'll still receive booking-related messages.",
        });
      } catch (err) {
        setState({
          kind: 'error',
          message: apiErrorMessage(
            err,
            'This unsubscribe link is invalid or has expired.',
          ),
        });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />
      <main className="flex-1 max-w-[560px] w-full mx-auto px-4 py-16 text-center">
        {state.kind === 'loading' ? (
          <>
            <div className="w-12 h-12 mx-auto rounded-full border-4 border-[#8e2a2f]/20 border-t-[#8e2a2f] animate-spin mb-6" />
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px]">
              Unsubscribing…
            </h1>
          </>
        ) : state.kind === 'done' ? (
          <>
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] md:text-[34px] leading-[40px] md:leading-[48px] mb-3">
              You're unsubscribed.
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-8">
              {state.message}
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mb-8">
              Changed your mind? Sign in and re-enable marketing emails from{' '}
              <Link to="/account-settings" className="text-[#8e2a2f] underline">
                account settings
              </Link>
              .
            </p>
            <Link to="/" className="inline-block">
              <PrimaryButton className="w-auto px-8 !h-[44px]">
                Back to Cherry Picked
              </PrimaryButton>
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] md:text-[34px] leading-[40px] md:leading-[48px] mb-3">
              We couldn't unsubscribe
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-8">
              {state.message}
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mb-8">
              You can also turn marketing off from{' '}
              <Link to="/account-settings" className="text-[#8e2a2f] underline">
                account settings
              </Link>{' '}
              once signed in.
            </p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
